import {
  PDF_PAGE_WIDTH_PT,
  PDF_FONT_SIZE_OVERRIDES,
  PDF_WIDTH_TRANSFERS_PT,
  rowsPerPageFor,
} from './reportPdfProfiles';
import { formatDateOnly, formatTimestamp } from '../../../utils/dateTime';

/**
 * Turns a report config plus its rows into the props ReportPdfDocument renders. The ONE prep
 * path for report PDFs: the viewer and the server-side renderer both call it, so a downloaded
 * PDF and an emailed one cannot differ.
 *
 * Nothing here may touch the DOM, window or the network; it must run in the browser and in
 * Node. Anything environment-bound comes in as an argument:
 *   measureText(text, font) -> width in px. The browser passes a canvas measurer.
 *   clientLogoSrc / qrDataUrl / logoSrc arrive already resolved (data URLs or bundled assets).
 */

// Mirrors backend ToCamelCase: all-uppercase names (e.g. "DTC") go fully lowercase,
// others just lower the first character.
export const toCamelCase = (name) => {
  if (!name) return name;
  if ([...name].every(c => (c >= 'A' && c <= 'Z') || c === '_')) return name.toLowerCase();
  return name.charAt(0).toLowerCase() + name.slice(1);
};

// Date values in report rows: a SQL date column arrives as 'YYYY-MM-DD' and prints literally; a
// datetime arrives as an ISO instant (zoneless is read as UTC) and prints in `timeZone`, an IANA
// name. Undefined means the person's own zone, which is right in the browser; the server-side
// renderer always passes the subscription owner's zone because its own clock is UTC.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
export const fmtDate = (v, timeZone) => {
  if (typeof v !== 'string') return v;
  if (DATE_ONLY.test(v)) return formatDateOnly(v);
  if (ISO_TIMESTAMP.test(v)) return formatTimestamp(v, { displayZone: timeZone });
  return v;
};
// Format all date and datetime values in a row set before it is rendered
export const fmtDataRows = (rows, timeZone) => rows.map(row => Object.fromEntries(Object.entries(row).map(([k, v]) => [k, fmtDate(v, timeZone)])));

// Split a multi-word column header into at most 2 display lines.
// 1 word → [label, ''] | 2 words → [word1, word2] | 3+ → optimal split minimizing max line length
export const splitHeaderLabel = (label) => {
  const words = label.split(' ');
  if (words.length <= 1) return [label, ''];
  if (words.length === 2) return [words[0], words[1]];
  let bestSplit = 1, bestMax = Infinity;
  for (let i = 1; i < words.length; i++) {
    const m = Math.max(words.slice(0, i).join(' ').length, words.slice(i).join(' ').length);
    if (m < bestMax) { bestMax = m; bestSplit = i; }
  }
  return [words.slice(0, bestSplit).join(' '), words.slice(bestSplit).join(' ')];
};

// Reports that always group by a fixed field — grouping selector is locked for these.
export const FIXED_GROUP_BY = {
  'slugRptStd-Sub-Index_Report_Panels': 'Index_Sheet',
  'slugRptStd-Sub-Index_Report_Tags':   'Index_Sheet',
};

// Columns hidden from preview/PDF for Index reports (group field + spec-label columns)
export const INDEX_REPORT_HIDDEN_FIELDS = new Set([
  'Index_Sheet', 'IDX_SPEC_1_COL', 'IDX_SPEC_2_COL',
  'IDX_SPEC_3_COL', 'IDX_SPEC_4_COL', 'IDX_SPEC_5_COL',
]);
// Maps each spec-label field to the spec data field whose header it drives
export const INDEX_SPEC_COL_MAP = [
  ['IDX_SPEC_1_COL', 'IDX_SPEC_1'],
  ['IDX_SPEC_2_COL', 'IDX_SPEC_2'],
  ['IDX_SPEC_3_COL', 'IDX_SPEC_3'],
  ['IDX_SPEC_4_COL', 'IDX_SPEC_4'],
  ['IDX_SPEC_5_COL', 'IDX_SPEC_5'],
];
// Returns cols with each Spec N column's label replaced by the IDX_SPEC_N_COL value in firstRow.
// IDX_SPEC_N_COL has fkSourceField so the backend only returns it as {camel}_Display, not raw.
export const applyIndexSpecLabels = (cols, firstRow) =>
  cols.map(c => {
    const entry = INDEX_SPEC_COL_MAP.find(([, sf]) => sf === c.field);
    if (!entry || !firstRow) return c;
    const camelColField = toCamelCase(entry[0]);
    const label = firstRow[`${camelColField}_Display`] || firstRow[camelColField] || c.label;
    return { ...c, label };
  });

// Display value of a field on a row: the resolved _Display value when there is one.
const displayValue = (row, camelField) =>
  row[`${camelField}_Display`] != null ? row[`${camelField}_Display`] : row[camelField];

/** Sort rows by [{ field, direction }] in precedence order, on display values. */
export const sortReportRows = (rows, sortOrder) => {
  if (!sortOrder || sortOrder.length === 0) return rows;

  return [...rows].sort((a, b) => {
    for (const { field, direction } of sortOrder) {
      const camelField = toCamelCase(field);
      const aVal = displayValue(a, camelField);
      const bVal = displayValue(b, camelField);

      if (aVal == null && bVal == null) continue;
      if (aVal == null) return direction === 'asc' ? 1 : -1;
      if (bVal == null) return direction === 'asc' ? -1 : 1;

      let cmp;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      } else {
        cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
      }

      if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
    }
    return 0;
  });
};

/**
 * Parameter-header column widths (PDF pt) from each parameter's option list:
 * results = [{ field, label, options }].
 *
 * Sized by character count, capped. For fullDisplay fields (headerDisplayFormat concatenations
 * like "NUM - DESC") the prefix and suffix lengths are tracked independently, so the worst-case
 * combination (longest number from one row + longest desc from another) is used rather than a
 * single row's total length. 4pt Helvetica × 0.625 char ratio = 2.5pt/char, +6 = paddingLeft;
 * 0.625 is calibrated for uppercase engineering labels (panel IDs, tag names, etc).
 */
export const computeParamHeaderWidths = (results) => {
  const widths = {};
  results.forEach(({ field, label, options }) => {
    const hasFullDisplay = options.some(o => o.fullDisplay);
    let maxChars = label.length;
    if (hasFullDisplay) {
      let maxPrefixLen = 0, maxSuffixLen = 0;
      options.forEach(o => {
        const fd = String(o.fullDisplay || o.display || '');
        const sepIdx = fd.indexOf(' - ');
        if (sepIdx >= 0) {
          maxPrefixLen = Math.max(maxPrefixLen, sepIdx);
          maxSuffixLen = Math.max(maxSuffixLen, fd.length - sepIdx - 3);
        } else {
          maxPrefixLen = Math.max(maxPrefixLen, fd.length);
        }
      });
      maxChars = Math.max(maxChars, maxPrefixLen + (maxSuffixLen > 0 ? 3 + Math.min(maxSuffixLen, 70) : 0));
    } else {
      options.forEach(o => {
        maxChars = Math.max(maxChars, String(o.display || '').length);
      });
    }
    widths[field] = Math.max(40, Math.min(Math.round(maxChars * 2.5) + 6, 310));
  });
  return widths;
};

/** Pages a report of rowCount rows renders to, at that report's real rows-per-page. */
export const estimatePdfPages = (rowCount, reportSlug) =>
  Math.ceil(Math.max(rowCount, 1) / rowsPerPageFor(reportSlug));

// Content-aware column widths: measure actual data values + header label, then distribute the
// remaining tabloid-landscape space by config proportion. Every value fits on one line (style
// guide: no truncation, no 2-line rows).
const WRAPPABLE_FIELDS_PDF = new Set(['Note', 'Comment', 'Note_DES']);
const CELL_PAD_PT = 4; // left+right padding in PDF cells

const computeColumnWidths = ({ cols, rows, fontSize, isIndexRpt, reportSlug, measureText, timeZone }) => {
  const dataFont   = `${fontSize}px Helvetica, sans-serif`;
  const headerFont = `bold ${fontSize}px Helvetica, sans-serif`;

  // Minimum width per column = max(header label width, longest data value width) + padding
  const minWidths = {};
  cols.forEach(c => {
    const camelField = toCamelCase(c.field);
    const isWrappable = WRAPPABLE_FIELDS_PDF.has(c.field);
    let maxDataPx = 0;
    if (!isWrappable) {
      rows.forEach(row => {
        const val = displayValue(row, camelField);
        if (val != null) {
          maxDataPx = Math.max(maxDataPx, measureText(String(fmtDate(val, timeZone)), dataFont));
        }
      });
    }
    // For Index report spec columns, header label = IDX_SPEC_N_COL value (varies per group).
    // Measure widest label across all rows so the column fits any group's header.
    let headerPx;
    if (isIndexRpt) {
      const specEntry = INDEX_SPEC_COL_MAP.find(([, sf]) => sf === c.field);
      if (specEntry) {
        // fkSourceField columns come back as {camel}_Display — check that key first
        const camelColField = toCamelCase(specEntry[0]);
        let maxLabelPx = 0;
        rows.forEach(row => {
          const lbl = row[`${camelColField}_Display`] || row[camelColField];
          if (lbl) maxLabelPx = Math.max(maxLabelPx, measureText(String(lbl), headerFont));
        });
        headerPx = maxLabelPx || measureText(c.label, headerFont);
      }
    }
    if (headerPx == null) {
      const [hLine1, hLine2] = splitHeaderLabel(c.label);
      headerPx = Math.max(measureText(hLine1, headerFont), hLine2 ? measureText(hLine2, headerFont) : 0);
    }
    // At 6pt font, CSS px measurements are ~25% too wide relative to PDF pt — apply 0.75.
    // At 8pt, px ≈ pt so factor is 1.0.
    const ptFactor = fontSize <= 6 ? 0.75 : 1.0;
    minWidths[c.field] = Math.max(40, Math.ceil(Math.max(maxDataPx, headerPx) * ptFactor) + CELL_PAD_PT);
  });
  // Wrappable column floors: Note ≥ Description width; Comment ≥ 175pt
  if (minWidths.Note != null) {
    minWidths.Note = Math.max(minWidths.Note, minWidths.Description || minWidths.Tag_Description || 50);
  }
  if (minWidths.Comment != null) {
    minWidths.Comment = Math.max(minWidths.Comment, 175);
  }
  if (minWidths.Note_DES != null) {
    minWidths.Note_DES = Math.max(minWidths.Note_DES, 175);
  }

  const totalMinPt = cols.reduce((sum, c) => sum + minWidths[c.field], 0);
  const colWidths = {};

  if (totalMinPt >= PDF_PAGE_WIDTH_PT) {
    // Mins already fill the page — just use them as-is
    cols.forEach(c => { colWidths[c.field] = `${((minWidths[c.field] / totalMinPt) * 100).toFixed(1)}%`; });
  } else {
    // Distribute remaining space proportionally by config pixelWidth preference
    const remaining = PDF_PAGE_WIDTH_PT - totalMinPt;
    const totalConfigPx = cols.reduce((sum, c) => sum + (c.pixelWidth || c.width * 10 || 100), 0);
    cols.forEach(c => {
      const configShare = (c.pixelWidth || c.width * 10 || 100) / totalConfigPx;
      const extraPt = remaining * configShare;
      colWidths[c.field] = `${(((minWidths[c.field] + extraPt) / PDF_PAGE_WIDTH_PT) * 100).toFixed(1)}%`;
    });
  }

  Object.entries(PDF_WIDTH_TRANSFERS_PT[reportSlug] || {}).forEach(([field, deltaPt]) => {
    if (colWidths[field] == null) return;
    const pct = parseFloat(colWidths[field]) + (deltaPt / PDF_PAGE_WIDTH_PT) * 100;
    colWidths[field] = `${pct.toFixed(1)}%`;
  });

  return colWidths;
};

/**
 * Build every PDF document a report export produces.
 *
 * @param {object}   opts
 * @param {object}   opts.config             report config (AppCfg_ModuleDynamic, Config_Type='report')
 * @param {string}   opts.reportSlug
 * @param {string}   opts.reportName         display name; also the base of separate-file names
 * @param {array}    opts.rows               rows ALREADY SORTED (sortReportRows)
 * @param {array}    [opts.sortOrder]        [{ field, direction }], printed in the header
 * @param {object}   opts.parameters         committed filter selections, printed in the header
 * @param {object}   opts.paramHeaderWidths  computeParamHeaderWidths result
 * @param {string}   [opts.groupBy]          group-by field
 * @param {boolean}  [opts.groupSeparate]    one document per group value
 * @param {string}   [opts.customTitle]
 * @param {string}   [opts.watermark]
 * @param {string}   opts.logoSrc
 * @param {string}   [opts.clientLogoSrc]
 * @param {string}   [opts.clientName]
 * @param {string}   [opts.qrDataUrl]
 * @param {string}   [opts.dawnVersion]
 * @param {string}   [opts.timeZone]       IANA zone datetimes print in; omitted = the person's zone
 * @param {function} opts.measureText        (text, cssFont) => px
 * @returns {{ pageCount: number, rowsPerPage: number, separate: boolean,
 *             documents: Array<{ reportName: string, props: object }> }}
 *   props is exactly what ReportPdfDocument / the PDF worker take (plus groupByLabel for the
 *   combined file's name).
 */
const prepareReportPdf = ({
  config,
  reportSlug,
  reportName,
  rows,
  sortOrder,
  parameters,
  paramHeaderWidths,
  groupBy,
  groupSeparate,
  customTitle,
  watermark,
  logoSrc,
  clientLogoSrc,
  clientName,
  qrDataUrl,
  dawnVersion,
  measureText,
  timeZone,
}) => {
  const isIndexRpt = !!FIXED_GROUP_BY[reportSlug];
  const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order)
    .filter(c => !isIndexRpt || !INDEX_REPORT_HIDDEN_FIELDS.has(c.field));

  const fontSizeOverride = PDF_FONT_SIZE_OVERRIDES[reportSlug];
  const fontSize = fontSizeOverride || config.layout?.fontSize || 8;
  const columnWidths = computeColumnWidths({ cols, rows, fontSize, isIndexRpt, reportSlug, measureText, timeZone });
  const rowsPerPage = rowsPerPageFor(reportSlug);

  // For Index reports: per-group column configs with dynamic spec headers. The combined PDF
  // passes columnsByGroup so ReportPdfDocument can change headers per group page; separate
  // files use the per-group cols directly.
  let columnsByGroup = null;
  if (isIndexRpt && groupBy) {
    const groupCf = toCamelCase(groupBy);
    const firstRowByGroup = {};
    rows.forEach(row => {
      const val = displayValue(row, groupCf);
      const key = val != null ? String(val) : '(Blank)';
      if (!firstRowByGroup[key]) firstRowByGroup[key] = row;
    });
    columnsByGroup = {};
    Object.entries(firstRowByGroup).forEach(([groupVal, firstRow]) => {
      columnsByGroup[groupVal] = applyIndexSpecLabels(cols, firstRow);
    });
  }

  // For Index reports: strip Index_Sheet from header params (shown as subtitle already).
  // Always strip label-type params (display-only, not SQL filters) and inject customTitle.
  const pdfHeaderConfig = {
    ...(isIndexRpt
      ? { ...config, parameters: (config.parameters || []).filter(p => p.field !== 'Index_Sheet' && p.type !== 'label' && p.type !== 'watermark') }
      : { ...config, parameters: (config.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark') }),
    ...(customTitle ? { customTitle } : {}),
    ...(watermark ? { watermark } : {})
  };

  const shared = {
    columnWidths,
    parameters,
    paramHeaderWidths,
    logoSrc,
    clientLogoSrc: clientLogoSrc || undefined,
    clientName: clientName || undefined,
    sortOrder: sortOrder && sortOrder.length > 0 ? sortOrder : undefined,
    rowsPerPage,
    fontSizeOverride,
    qrDataUrl: qrDataUrl || undefined,
    dawnVersion: dawnVersion || undefined,
  };

  const pageCount = estimatePdfPages(rows.length, reportSlug);

  if (groupBy && groupSeparate) {
    // Separate PDF file per unique group value, (Blank) last
    const groupMap = {};
    const cf = toCamelCase(groupBy);
    rows.forEach(row => {
      const val = displayValue(row, cf);
      const key = val != null ? String(val) : '(Blank)';
      if (!groupMap[key]) groupMap[key] = [];
      groupMap[key].push(row);
    });
    const documents = Object.entries(groupMap).sort(([a], [b]) => {
      if (a === '(Blank)') return 1;
      if (b === '(Blank)') return -1;
      return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
    }).map(([groupVal, groupRows]) => {
      // For Index reports: each separate file gets that group's dynamic spec labels
      const pageReportName = `${reportName} - ${groupVal}`;
      return {
        reportName: pageReportName,
        props: {
          ...shared,
          config: { ...pdfHeaderConfig, reportName: pageReportName },
          columns: columnsByGroup?.[groupVal] || cols,
          data: fmtDataRows(groupRows, timeZone),
          groupByField: groupBy, // still passed so the header subtitle renders per page
        },
      };
    });
    return { pageCount, rowsPerPage, separate: true, documents };
  }

  const groupByLabel = groupBy
    ? (config?.parameters?.find(p => p.field === groupBy)?.label
        || config?.columns?.find(c => c.field === groupBy)?.label
        || groupBy)
    : undefined;

  return {
    pageCount,
    rowsPerPage,
    separate: false,
    documents: [{
      reportName,
      props: {
        ...shared,
        config: { ...pdfHeaderConfig, reportName },
        columns: cols,
        data: fmtDataRows(rows, timeZone),
        groupByField: groupBy || undefined,
        groupByLabel,
        columnsByGroup: columnsByGroup || undefined,
      },
    }],
  };
};

export default prepareReportPdf;
